import { baseApi } from "../../../app/api";
import { store } from "../../../app/store";
import { formatMinorUnitString } from "../../../common/helpers/formatPrice"; // Import formatter
import type { FetchBaseQueryMeta } from "@reduxjs/toolkit/query";

// Import the RAW types for API responses
import type {
  RawProduct,
  RawProductsResponse,
  RawEnrichedAttribute,
  RawEnrichedAttributeTerm,
  RawEnrichedVariation,
  RawEnrichedVariationAttribute,
  RawImage,
  RawEnrichedCategory,
  RawEnrichedTag,
  RawProductBrandRef,
} from "../../../types/api/wcStoreApi/wcStoreApiProductsEndpointTypes";

// Import the final TRANSFORMED types and parameter types
import type {
  GetProductsParams,
  GetProductsResult,
  TransformedProduct,
  TransformedVariation,
  EnrichedAttributeTransformed,
  EnrichedAttributeTermTransformed,
  EnrichedVariationTransformed,
  EnrichedVariationAttributeTransformed,
  TransformedCategory,
  TransformedTag,
  TransformedBrand,
} from "../types/productApiTypes";

// --- API Slice Definition ---

const WC_STORE_API_BASE =
  window.themeVariables?.wcStoreApiBase || "/wp-json/wc/store/v1";

export const productApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Fetches products list. Uses RawProductsResponse, transforms to GetProductsResult.
     */
    getProducts: builder.query<GetProductsResult, GetProductsParams>({
      query: (params: GetProductsParams = {}) => {
        const queryParams = new URLSearchParams();
        const minorUnit = window.themeVariables?.currencyMinorUnit ?? 2;
        const multiplier = Math.pow(10, minorUnit);

        if (params.category && params.category.length)
          queryParams.append("category", params.category.join(","));
        if (params.tag && params.tag.length)
          queryParams.append("tag", params.tag.join(","));
        if (params.brand && params.brand.length)
          queryParams.append("brand", params.brand.join(","));
        if (params.search) queryParams.append("search", params.search);
        if (params.orderby) {
          switch (params.orderby) {
            case "popularity":
              queryParams.append("orderby", "popularity");
              break;
            case "date":
              queryParams.append("orderby", "date");
              break;
            case "price-asc":
              queryParams.append("orderby", "price");
              queryParams.append("order", "asc");
              break;
            case "price-desc":
              queryParams.append("orderby", "price");
              queryParams.append("order", "desc");
              break;
            default:
              queryParams.append("orderby", "date");
          }
        }
        if (typeof params.minPrice === "number") {
          const minPriceMinor = Math.round(params.minPrice * multiplier);
          queryParams.append("min_price", String(minPriceMinor));
        }
        if (typeof params.maxPrice === "number") {
          const maxPriceMinor = Math.round(params.maxPrice * multiplier);
          queryParams.append("max_price", String(maxPriceMinor));
        }
        if (params.onSale) queryParams.append("on_sale", "true");
        if (params.page) queryParams.append("page", String(params.page));
        if (params.limit) queryParams.append("per_page", String(params.limit));

        return {
          url: `${WC_STORE_API_BASE}/products`,
          params: queryParams,
        };
      },
      transformResponse: (
        response: RawProductsResponse,
        meta: FetchBaseQueryMeta | undefined
      ): GetProductsResult => {
        const rawProducts: RawProduct[] = Array.isArray(response)
          ? response
          : [];
        const headers = meta?.response?.headers;
        const totalProducts = parseInt(headers?.get("X-WP-Total") || "0", 10);
        const totalPages = parseInt(headers?.get("X-WP-TotalPages") || "1", 10);

        const transformedProducts = rawProducts
          .map(transformProductData)
          .filter((p): p is TransformedProduct => p !== null);

        return {
          products: transformedProducts,
          pagination: { totalProducts, totalPages },
        };
      },
      providesTags: (result) =>
        result?.products
          ? [
              ...result.products.map(({ id }) => ({
                type: "Products" as const,
                id,
              })),
              { type: "Products" as const, id: "LIST" },
            ]
          : [{ type: "Products" as const, id: "LIST" }],
    }),

    /**
     * Fetches single product by ID. Uses RawProduct, transforms to TransformedProduct.
     */
    getProductById: builder.query<TransformedProduct | null, number | string>({
      query: (id) => ({ url: `${WC_STORE_API_BASE}/products/${id}` }),
      transformResponse: (product: RawProduct): TransformedProduct | null =>
        transformProductData(product),
      providesTags: (result, error, id) => [{ type: "Products" as const, id }],
    }),

    /**
     * Fetches single product by Slug. Uses RawProductsResponse, transforms first item.
     */
    getProductBySlug: builder.query<TransformedProduct | null, string>({
      query: (slug) => ({
        url: `${WC_STORE_API_BASE}/products`,
        params: { slug },
      }),
      transformResponse: (
        response: RawProductsResponse
      ): TransformedProduct | null => {
        const products: RawProduct[] = Array.isArray(response) ? response : [];
        return products.length > 0 ? transformProductData(products[0]) : null;
      },
      providesTags: (result) =>
        result ? [{ type: "Products" as const, id: result.id }] : [],
    }),

    /**
     * Fetches related products by ID. Uses RawProductsResponse, transforms each item.
     */
    getRelatedProducts: builder.query<
      TransformedProduct[],
      (number | string)[]
    >({
      query: (productIds) => ({
        url: `${WC_STORE_API_BASE}/products`,
        params: {
          include: productIds ? productIds.join(",") : "",
          per_page: productIds.length > 0 ? productIds.length : 10,
        },
      }),
      transformResponse: (
        response: RawProductsResponse
      ): TransformedProduct[] => {
        const products: RawProduct[] = Array.isArray(response) ? response : [];
        return products
          .map(transformProductData)
          .filter((p): p is TransformedProduct => p !== null);
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Products" as const, id })),
              { type: "Products" as const, id: "LIST" },
            ]
          : [{ type: "Products" as const, id: "LIST" }],
    }),

    /**
     * Fetches variations for a product. Uses RawProductsResponse, transforms to TransformedVariation[].
     */
    getProductVariations: builder.query<
      TransformedVariation[],
      number | string
    >({
      query: (productId) => ({
        url: `${WC_STORE_API_BASE}/products`,
        params: { type: "variation", parent: productId, per_page: 100 },
      }),
      transformResponse: (
        response: RawProductsResponse
      ): TransformedVariation[] => {
        const variations: RawProduct[] = Array.isArray(response)
          ? response
          : [];
        return variations.map(transformVariationData); // Use refactored transformer
      },
      providesTags: (result, error, productId) => [
        { type: "Variations" as const, id: productId },
      ],
    }),
  }),
  overrideExisting: false,
});

// --- Transformation Functions ---

export const transformProductData = (
  product: RawProduct | null | undefined
): TransformedProduct | null => {
  if (!product) return null;

  // --- Currency Info ---
  const currencyMinorUnit = window.themeVariables?.currencyMinorUnit ?? 2;

  // --- Extract Extensions Safely ---
  const extensions = product.extensions || {};
  const defaultVariation =
    !Array.isArray(extensions.default_variation) && extensions.default_variation
      ? extensions.default_variation
      : null;
  const relatedProducts =
    !Array.isArray(extensions.related_products) && extensions.related_products
      ? extensions.related_products
      : { related_ids: [] };
  const translations =
    !Array.isArray(extensions.product_translations) &&
    extensions.product_translations
      ? extensions.product_translations
      : {};
  const enrichedAttributesData = extensions.enriched_attributes_data || {
    attributes: [],
  };
  const enrichedVariationsData =
    !Array.isArray(extensions.enriched_variations_data) &&
    extensions.enriched_variations_data
      ? extensions.enriched_variations_data
      : { enriched_variations: [] };
  const enrichedCategoriesData = extensions.enriched_categories_data || {
    categories: [],
  };
  const enrichedTagsData = extensions.enriched_tags_data || { tags: [] };
  const variationGalleryData = extensions.variation_gallery || { items: [] };

  // --- Language & Translation Logic ---
  const state = store.getState();
  const currentLanguage = state.language.currentLanguage;
  const defaultLanguage = state.language.defaultLanguage;
  const shouldTranslate = currentLanguage !== defaultLanguage;

  let finalName = product.name;
  let finalDescription = product.description || "";
  let finalShortDescription = product.short_description || "";

  if (shouldTranslate && translations && translations[currentLanguage]) {
    const translation = translations[currentLanguage];
    if (translation.name) finalName = translation.name;
    if (translation.description) finalDescription = translation.description;
    if (translation.short_description)
      finalShortDescription = translation.short_description;
  }

  // --- Price, Stock, Image, SKU Logic (using defaultVariation if applicable) ---
  const hasDefaultVariation = !!defaultVariation;
  const rawPrice = hasDefaultVariation
    ? defaultVariation.price
    : product.prices.price;
  const rawRegularPrice = hasDefaultVariation
    ? defaultVariation.regular_price
    : product.prices.regular_price;
  const rawSalePrice = hasDefaultVariation
    ? defaultVariation.sale_price
    : product.prices.sale_price;

  const divisor = Math.pow(10, currencyMinorUnit);
  const price = parseFloat(rawPrice) / divisor;
  const regularPrice = parseFloat(rawRegularPrice) / divisor;
  const salePrice = parseFloat(rawSalePrice) / divisor;

  // Format prices
  const formattedPrice = formatMinorUnitString(rawPrice, currencyMinorUnit);
  const formattedRegularPrice = formatMinorUnitString(
    rawRegularPrice,
    currencyMinorUnit
  );
  const formattedSalePrice = formatMinorUnitString(
    rawSalePrice,
    currencyMinorUnit
  );

  const onSale = hasDefaultVariation
    ? defaultVariation.on_sale
    : product.on_sale;
  let stockStatus: TransformedProduct["stockStatus"] = "outofstock";
  let stockQuantity: number | null | undefined = undefined;
  if (hasDefaultVariation) {
    stockStatus =
      defaultVariation.stock_status as TransformedProduct["stockStatus"];
    stockQuantity = defaultVariation.stock_quantity;
  } else {
    if (product.is_in_stock)
      stockStatus = product.is_on_backorder ? "onbackorder" : "instock";
    stockQuantity =
      product.low_stock_remaining !== null
        ? product.low_stock_remaining
        : undefined;
  }
  const defaultImage = defaultVariation?.image;
  const productImages = product.images || [];
  const finalImages: RawImage[] = defaultImage
    ? [
        defaultImage,
        ...productImages.filter((img) => img.id !== defaultImage.id),
      ]
    : productImages;
  const sku = hasDefaultVariation ? defaultVariation.sku : product.sku;

  // --- Transform Enriched Categories ---
  const transformedEnrichedCategories: TransformedCategory[] = (
    enrichedCategoriesData.categories || []
  ).map(
    (rawCat: RawEnrichedCategory): TransformedCategory => ({
      id: rawCat.id,
      name:
        shouldTranslate && rawCat.translated_name
          ? rawCat.translated_name
          : rawCat.name,
      slug: rawCat.slug,
      link: rawCat.link,
    })
  );

  // --- Transform Enriched Tags ---
  const transformedEnrichedTags: TransformedTag[] = (
    enrichedTagsData.tags || []
  ).map(
    (rawTag: RawEnrichedTag): TransformedTag => ({
      id: rawTag.id,
      name:
        shouldTranslate && rawTag.translated_name
          ? rawTag.translated_name
          : rawTag.name,
      slug: rawTag.slug,
      link: rawTag.link,
    })
  );

  // --- Transform Enriched Brands (Now using product.brands) ---
  const transformedEnrichedBrands: TransformedBrand[] = (
    product.brands || []
  ).map(
    (rawBrand: RawProductBrandRef): TransformedBrand => ({
      id: rawBrand.id,
      name: rawBrand.name,
      slug: rawBrand.slug,
      link: rawBrand.link,
    })
  );

  // --- Transform Enriched Attributes (including Terms) ---
  const transformedEnrichedAttributes: EnrichedAttributeTransformed[] = (
    enrichedAttributesData.attributes || []
  ).map((rawAttr: RawEnrichedAttribute): EnrichedAttributeTransformed => {
    const termsTransformed: EnrichedAttributeTermTransformed[] = (
      rawAttr.terms || []
    ).map(
      (
        rawTerm: RawEnrichedAttributeTerm
      ): EnrichedAttributeTermTransformed => ({
        id: rawTerm.id,
        name:
          shouldTranslate && rawTerm.translated_name
            ? rawTerm.translated_name
            : rawTerm.name, // Translate term name
        slug: rawTerm.slug,
        type: rawTerm.type,
        color: rawTerm.color,
        buttonText:
          shouldTranslate && rawTerm.translated_name
            ? rawTerm.translated_name
            : rawTerm.name, // Translate term name
        buttonImage: rawTerm["button-image"],
      })
    );
    return {
      id: rawAttr.id,
      name:
        shouldTranslate && rawAttr.translated_name
          ? rawAttr.translated_name
          : rawAttr.name, // Translate attribute name
      taxonomy: rawAttr.taxonomy,
      hasVariations: rawAttr.has_variations,
      displayType: rawAttr.display_type,
      terms: termsTransformed,
    };
  });

  // --- Transform Enriched Variations (including their Attributes and Terms) ---
  const transformedEnrichedVariations: EnrichedVariationTransformed[] = (
    enrichedVariationsData.enriched_variations || []
  ).map((rawVar: RawEnrichedVariation): EnrichedVariationTransformed => {
    const transformedAttributes: EnrichedVariationAttributeTransformed[] = (
      rawVar.enriched_attributes_data?.attributes || []
    ).map(
      (
        varAttr: RawEnrichedVariationAttribute
      ): EnrichedVariationAttributeTransformed => {
        const transformedTerm: EnrichedAttributeTermTransformed | undefined =
          varAttr.terms[0]
            ? {
                id: varAttr.terms[0].id,
                name:
                  shouldTranslate && varAttr.terms[0].translated_name
                    ? varAttr.terms[0].translated_name
                    : varAttr.terms[0].name, // Translate term name
                slug: varAttr.terms[0].slug,
                type: varAttr.terms[0].type,
                color: varAttr.terms[0].color,
                buttonText:
                  shouldTranslate && varAttr.terms[0].translated_name
                    ? varAttr.terms[0].translated_name
                    : varAttr.terms[0].name, // Translate term name
                buttonImage: varAttr.terms[0]["button-image"],
              }
            : undefined;

        return {
          id: varAttr.id,
          name:
            shouldTranslate && varAttr.translated_name
              ? varAttr.translated_name
              : varAttr.name, // Translate attribute name
          value: varAttr.value,
          taxonomy: varAttr.taxonomy,
          hasVariations: varAttr.has_variations,
          displayType: varAttr.display_type,
          terms: transformedTerm ? [transformedTerm] : [],
        };
      }
    );
    return {
      id: rawVar.id,
      attributes: transformedAttributes,
    };
  });

  // --- Build Final Transformed Product ---
  const transformedProduct: TransformedProduct = {
    id: product.id,
    name: finalName,
    slug: product.slug,
    permalink: product.permalink,
    price: price,
    regularPrice: regularPrice,
    salePrice: salePrice,
    formattedPrice: formattedPrice, // Add formatted price
    formattedRegularPrice: formattedRegularPrice, // Add formatted regular price
    formattedSalePrice: formattedSalePrice, // Add formatted sale price
    priceHtml: product.price_html || "",
    onSale: onSale,
    stockStatus: stockStatus,
    stockQuantity: stockQuantity,
    featured: (product as any).featured || false,
    description: finalDescription,
    shortDescription: finalShortDescription,
    averageRating: product.average_rating || "0",
    images: finalImages,
    sku: sku || "",
    type: product.type,
    enrichedCategories: transformedEnrichedCategories, // Use transformed
    enrichedTags: transformedEnrichedTags, // Use transformed
    enrichedBrands: transformedEnrichedBrands, // Use transformed brands from product.brands
    enrichedAttributes: transformedEnrichedAttributes, // Use transformed
    enrichedVariations: transformedEnrichedVariations, // Use transformed
    isVariable: product.type === "variable",
    hasDefaultVariation: hasDefaultVariation,
    defaultVariationData: defaultVariation, // Use RawDefaultVariation (with attributes:[])
    relatedProductIds: relatedProducts.related_ids || [],
    variationGallery: variationGalleryData.items || [], // Use gallery items
  };

  return transformedProduct;
};

/**
 * Transforms raw variation data (RawProduct of type 'variation') into TransformedVariation.
 */
const transformVariationData = (
  variation: RawProduct
): TransformedVariation => {
  if (!variation || variation.type !== "variation") {
    console.error(
      "transformVariationData: Input is not a variation product:",
      variation
    );
    return {
      id: variation?.id || 0,
      productId: variation?.parent || 0,
      sku: "",
      price: undefined,
      regularPrice: undefined,
      salePrice: undefined,
      formattedPrice: "", // Add formatted price
      formattedRegularPrice: "", // Add formatted regular price
      formattedSalePrice: "", // Add formatted sale price
      onSale: false,
      purchasable: false,
      virtual: false,
      downloadable: false,
      stockStatus: "outofstock",
      stockQuantity: null,
      image: {},
      variationGallery: [],
    } as TransformedVariation;
  }

  const extensions = variation.extensions || {};
  const variationGallery = extensions.variation_gallery || { items: [] };

  const prices = variation.prices || {};
  const currencyMinorUnit = window.themeVariables?.currencyMinorUnit ?? 2;
  const divisor = Math.pow(10, currencyMinorUnit);

  let stockQuantity: number | null = null;
  if (variation.low_stock_remaining !== null)
    stockQuantity = variation.low_stock_remaining;

  let stockStatus: TransformedVariation["stockStatus"] = "outofstock";
  if (variation.is_in_stock)
    stockStatus = variation.is_on_backorder ? "onbackorder" : "instock";

  const transformedVariation: TransformedVariation = {
    id: variation.id,
    productId: variation.parent,
    sku: variation.sku || "",
    price: parseFloat(prices.price) / divisor,
    regularPrice: parseFloat(prices.regular_price) / divisor,
    salePrice: parseFloat(prices.sale_price) / divisor,
    formattedPrice: formatMinorUnitString(prices.price, currencyMinorUnit), // Add formatted price
    formattedRegularPrice: formatMinorUnitString(
      prices.regular_price,
      currencyMinorUnit
    ), // Add formatted regular price
    formattedSalePrice: formatMinorUnitString(
      prices.sale_price,
      currencyMinorUnit
    ), // Add formatted sale price
    onSale: variation.on_sale,
    purchasable: variation.is_purchasable,
    virtual: (variation as any).virtual || false,
    downloadable: (variation as any).downloadable || false,
    stockStatus: stockStatus,
    stockQuantity: stockQuantity,
    image: variation.images?.[0] || {},
    variationGallery: variationGallery.items || [],
    weight: (variation as any).weight ?? null,
    dimensions: (variation as any).dimensions ?? null,
  };

  return transformedVariation;
};

// --- Exports ---
export const {
  useGetProductsQuery,
  useLazyGetProductsQuery,
  useGetProductByIdQuery,
  useGetProductBySlugQuery,
  useGetProductVariationsQuery,
  useGetRelatedProductsQuery,
  util: { getRunningQueriesThunk },
} = productApi;

export const {
  getProducts,
  getProductById,
  getProductBySlug,
  getProductVariations,
  getRelatedProducts,
} = productApi.endpoints;
